import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Header, Table, Input, Button, Icon, Label, Pagination, Modal, Message } from 'react-frontier';
import { arrayMoveImmutable } from 'array-move';
import SortableList, { SortableItem } from 'react-easy-sort';
import { firstBy } from 'thenby';
import { useBreadcrumbs, useUser } from '../Hooks';
import { MetadataItem, SetLoading } from '../SiteClasses';
import { bindClick, getMetadataTypeName } from '../SiteUtil';
import UserAccess from '../UserAccess';
import Toast from '../Toast';
import API from '../API';

const PAGE_SIZE = 20;

enum Modals{
	SORT = 1,
	DELETE = 2,
}

var Forms = ()=>{
	var navigate = useNavigate();
	var { setTitle } = useBreadcrumbs();
	var { hasAccess } = useUser();
	var canCreate = hasAccess(UserAccess.FORMS.CREATE_FORMS);
	var canEditForm = hasAccess(UserAccess.FORMS.EDIT_FORMS);
	var canDeleteForm = hasAccess(UserAccess.FORMS.DELETE_FORMS);
	var [loadError, setLoadError] = useState<string>(null);
	var [forms, setForms] = useState<MetadataItem[]>(null);
	var [filter, setFilter] = useState<string>('');
	var [searchForms, setSearchForms] = useState<MetadataItem[]>(null);
	var [searchTimeout, setSearchTimeout] = useState<any>(null);
	var [searching, setSearching] = useState<boolean>(false);
	var [loading, setLoading] = useState<boolean>(false);
	var [page, setPage] = useState<number>(0);
	var [shownModal, setShownModal] = useState<number>(null);
	var [sortForms, setSortForms] = useState<MetadataItem[]>([]);
	var [sortError, setSortError] = useState<string>(null);
	var [selectedForm, setSelectedForm] = useState<MetadataItem>(null);
	var [deleteError, setDeleteError] = useState<string>(null);

	useEffect(()=>{
		setTitle('Formularios');
		get();
	}, []);

	var get = (get_page?: number)=>{
		var offset = (get_page > 0) ? Math.max(0, (get_page || page) * PAGE_SIZE) : 0;
		setLoading(true);
		API.getMetadatasList(PAGE_SIZE, offset).then(res=>{
			if(res.error) return setLoadError(res.message);
			setForms(res.data);
		}).catch(()=>{
			setLoadError('Hubo un error al obtener los formularios. (GFE-1)');
		}).finally(()=>{
			setLoading(false);
		});
	};

	var onPageChange = (pg: number)=>{
		setLoading(true);
		setPage(pg);
		get(pg);
	}

	var onSearchChange = (val: string)=>{
		setFilter(val);
		if(searchTimeout) clearTimeout(searchTimeout);
		if(val.length < 3) return setSearchForms(null);
		setSearchTimeout(setTimeout(()=>{ search(val); }, 500));
	}

	var search = (query: string)=>{
		setSearching(true);
		setSearchForms(null);
		API.searchMetadata(query).then(res=>{
			if(res.error) return setLoadError(res.message);
			setSearchForms(res.data);
		}).catch(()=>{
			setLoadError('Hubo un error al buscar el formulario. (SFS-1)');
		}).finally(()=>{
			setSearching(false);
		});
	}

	var showSort = ()=>{
		setSortForms(forms);
		setSortError(null);
		setShownModal(Modals.SORT);
	}

	var onSortEnd = (old: number, newix: number)=>{
		setSortForms(arr=>arrayMoveImmutable(arr, old, newix));
	}

	var saveSort = (setLoadingSort: SetLoading)=>{
		var order = sortForms.map(a=>a.metadata_id);
		setSortError(null);
		setLoadingSort(true);
		API.saveMetadataSort(order).then(res=>{
			if(res.error) return setSortError(res.message);
			var frms = [...forms];
			for(var i of frms){
				var ix = order.findIndex(a=>a===i.metadata_id);
				i.sort_order = ix === -1 ? 0 : order.length - ix;
			}
			setForms(frms);
			setShownModal(null);
		}).catch(()=>{
			setSortError('Hubo un error actualizando el orden (FSL-1)');
		}).finally(()=>{
			setLoadingSort(false);
		});
	}

	var onToggleDelete = (form: MetadataItem)=>{
		setSelectedForm(form);
		setDeleteError(null);
		setShownModal(Modals.DELETE);
	}

	var submitToggleDelete = (setLoadingDelete: SetLoading)=>{
		setDeleteError(null);
		setLoadingDelete(true);
		var is_restore = !!selectedForm.deleted;
		API.deleteMetadata(selectedForm.metadata_id, !is_restore).then(res=>{
			if(res.error) return setDeleteError(res.message);
			Toast.success(is_restore ? 'Formulario restaurado exitosamente' : 'Formulario eliminado exitosamente');
			var update = (arr: MetadataItem[])=>arr ? arr.map(f=>f.metadata_id === selectedForm.metadata_id ? { ...f, deleted: !is_restore } : f) : arr;
			setForms(prev=>update(prev));
			setSearchForms(prev=>update(prev));
			setShownModal(null);
		}).catch(()=>{
			setDeleteError(`Hubo un error inesperado ${is_restore ? 'restaurando' : 'eliminando'} el formulario. (FDL-1)`);
		}).finally(()=>{
			setLoadingDelete(false);
		});
	}

	if(loadError) return <Header text='Error' subtext={loadError} />;
	if(!forms) return <Header text='Cargando datos' loading />;

	var shown_forms = searchForms || forms;

	return <div>
		<Table
			style={{ maxWidth: 800, margin: 'auto', marginTop: 20 }}
			striped
		>
			<Table.Head
				title='Formularios'
				titleRight={(canEditForm || canCreate) ? (
					<div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
						{canEditForm && <Button icon iconName='sort' size='small' onClick={showSort} />}
						{canCreate && <Button iconName='plus' color='black' text='Agregar' size='small' as={Link} to='/forms/create' />}
					</div>
				) : undefined}
			>
				<Table.Row>
					<Table.Cell colSpan={6} style={{ padding: 8 }}>
						<Input fluid placeholder='Buscar...' value={filter} onChange={onSearchChange} iconName='search' style={{ margin: 0 }} />
					</Table.Cell>
				</Table.Row>
				<Table.Row>
					<Table.Cell header collapsing centered>ID</Table.Cell>
					<Table.Cell header centered>Pregunta</Table.Cell>
					<Table.Cell header collapsing centered>Tipo</Table.Cell>
					<Table.Cell header centered>Comentario</Table.Cell>
					<Table.Cell header collapsing centered>Estado</Table.Cell>
					<Table.Cell header collapsing centered><Icon name='wrench' /></Table.Cell>
				</Table.Row>
			</Table.Head>
			<Table.Body>
				{searching || loading ? (
					<Table.Row>
						<Table.Cell colSpan={6} centered>
							<Header loading text='Cargando datos' />
						</Table.Cell>
					</Table.Row>
				) : shown_forms.length === 0 ? (
					<Table.Row>
						<Table.Cell colSpan={6} centered>
							No se encontraron registros
						</Table.Cell>
					</Table.Row>
				) : (
					shown_forms.sort(firstBy('sort_order', 'desc')).map(f=>(
						<Table.Row key={f.metadata_id}>
							<Table.Cell collapsing>{f.metadata_id}</Table.Cell>
							<Table.Cell>{f.metadata_name}</Table.Cell>
							<Table.Cell collapsing>{getMetadataTypeName(f.type_id)}</Table.Cell>
							<Table.Cell centered={!f.metadata_comment}>{f.metadata_comment || <Icon name='minus' />}</Table.Cell>
							<Table.Cell collapsing>
								<Label color={f.deleted ? 'red' : 'green'}>{f.deleted ? 'Inactivo' : 'Activo'}</Label>
							</Table.Cell>
							<Table.Cell collapsing centered className='no-select'>
								{(canEditForm || canDeleteForm) ? (
									<Button basic icon iconName='ellipsis' size='small' menu={[
										{ text: 'Editar', iconName: 'pen', onClick: ()=>navigate(`/forms/${f.metadata_id}`), if: canEditForm },
										f.deleted
											? { text: 'Restaurar', iconName: 'trash-restore', onClick: ()=>onToggleDelete(f), if: canDeleteForm }
											: { text: 'Eliminar', iconName: 'trash', onClick: ()=>onToggleDelete(f), if: canDeleteForm },
									]} />
								) : <Icon name='minus' />}
							</Table.Cell>
						</Table.Row>
					))
				)}
			</Table.Body>
		</Table>
		{(shown_forms.length >= PAGE_SIZE || page !== 0) && (
			<div style={{ display: 'flex', justifyContent: 'flex-end', width: 900, margin: 'auto', marginTop: 10 }}>
				<Pagination page={page} onPageChange={onPageChange} hasNext={shown_forms.length >= PAGE_SIZE} />
			</div>
		)}
		<Modal open={shownModal===Modals.SORT} className='sort-forms-list' onClose={bindClick(setShownModal, null)}>
			<Modal.Header text='Ordenar formularios' />
			<Modal.Content style={{ position: 'relative', zIndex: 1, maxHeight: 600, overflowY: 'auto', overflowX: 'hidden' }}>
				{!!sortError && <Message type='error' list={[sortError]} style={{ marginBottom: 10 }} />}
				<SortableList
					onSortEnd={onSortEnd}
					className='list'
					draggedItemClassName='sort-forms-list-item-dragged'
				>
					<style>{`.sort-forms-list-item-dragged { z-index: 9999 !important; }`}</style>
					{sortForms.map(a=>(
						<SortableItem key={`SRT-FORM-${a.metadata_id}`}>
							<div className='item' style={{ padding: 10, marginBottom: 5, backgroundColor: '#f5f5f5', borderRadius: 4, cursor: 'move' }}>
								<div style={{ fontWeight: 'bold' }}>{a.metadata_name}</div>
								<div style={{ fontSize: '0.9em', color: '#666' }}>{getMetadataTypeName(a.type_id)}</div>
							</div>
						</SortableItem>
					))}
				</SortableList>
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic size='small' onClick={bindClick(setShownModal, null)} />
				<Button text='Guardar' color='black' size='small' onClick={saveSort} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===Modals.DELETE} onClose={bindClick(setShownModal, null)} size='tiny'>
			<Modal.Header text={selectedForm?.deleted ? 'Restaurar formulario' : 'Eliminar formulario'} />
			<Modal.Content>
				{!!selectedForm && (
					<Header
						text={selectedForm.deleted ? '¿Restaurar formulario?' : '¿Eliminar formulario?'}
						subtext={`Se ${selectedForm.deleted ? 'restaurará' : 'eliminará'} la pregunta "${selectedForm.metadata_name}"`}
						size='small'
					/>
				)}
				{!!deleteError && <Message type='error' list={[deleteError]} />}
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic size='small' onClick={bindClick(setShownModal, null)} />
				<Button
					text={selectedForm?.deleted ? 'Restaurar' : 'Eliminar'}
					color={selectedForm?.deleted ? 'black' : 'red'}
					size='small'
					onClick={submitToggleDelete}
				/>
			</Modal.Actions>
		</Modal>
	</div>
}

export default Forms;
